import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Eye, EyeOff, CheckCircle, XCircle, Loader2, Settings, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SettingsData {
  anthropicApiKey: string;
  openaiApiKey: string;
  anthropicConfigured: boolean;
  openaiConfigured: boolean;
  anthropicFromEnv: boolean;
  openaiFromEnv: boolean;
}

interface ApiKeySettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  missingService?: "anthropic" | "openai";
}

export function ApiKeySettingsDialog({ open, onOpenChange, missingService }: ApiKeySettingsDialogProps) {
  const { toast } = useToast();
  const [showAnthropic, setShowAnthropic] = useState(false);
  const [showOpenAI, setShowOpenAI] = useState(false);
  const [anthropicInput, setAnthropicInput] = useState("");
  const [openaiInput, setOpenaiInput] = useState("");
  const [testingService, setTestingService] = useState<"anthropic" | "openai" | null>(null);

  const { data: settings, isLoading } = useQuery<SettingsData>({
    queryKey: ["/api/settings"],
    enabled: open,
  });

  const saveMutation = useMutation({
    mutationFn: (data: { anthropicApiKey?: string; openaiApiKey?: string }) =>
      apiRequest("POST", "/api/settings", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/settings"] });
      setAnthropicInput("");
      setOpenaiInput("");
      toast({ title: "Einstellungen gespeichert", description: "Die API-Keys wurden erfolgreich gespeichert." });
    },
    onError: () => {
      toast({ title: "Fehler", description: "Einstellungen konnten nicht gespeichert werden.", variant: "destructive" });
    },
  });

  const testMutation = useMutation({
    mutationFn: async (params: { service: "anthropic" | "openai"; keyValue?: string }) => {
      const res = await apiRequest("POST", "/api/settings/test", params);
      return await res.json() as { ok: boolean; message: string };
    },
    onSuccess: (result) => {
      setTestingService(null);
      toast({
        title: result.ok ? "Verbindung erfolgreich" : "Verbindung fehlgeschlagen",
        description: result.message,
        variant: result.ok ? "default" : "destructive",
      });
    },
    onError: (error: unknown) => {
      setTestingService(null);
      const msg = error instanceof Error ? error.message : "Verbindungstest fehlgeschlagen";
      toast({ title: "Verbindung fehlgeschlagen", description: msg, variant: "destructive" });
    },
  });

  const handleSave = () => {
    const payload: { anthropicApiKey?: string; openaiApiKey?: string } = {};
    if (anthropicInput !== "") payload.anthropicApiKey = anthropicInput;
    if (openaiInput !== "") payload.openaiApiKey = openaiInput;
    if (Object.keys(payload).length === 0) {
      toast({ title: "Keine Änderungen", description: "Bitte geben Sie mindestens einen neuen API-Key ein." });
      return;
    }
    saveMutation.mutate(payload);
  };

  const handleTest = (service: "anthropic" | "openai") => {
    setTestingService(service);
    const keyValue =
      service === "anthropic" ? (anthropicInput || undefined) : (openaiInput || undefined);
    testMutation.mutate({ service, keyValue });
  };

  const ConfigBadge = ({ configured, fromEnv }: { configured: boolean; fromEnv: boolean }) => {
    if (fromEnv) {
      return <Badge variant="outline" className="text-xs text-blue-600 border-blue-300 bg-blue-50">Umgebungsvariable</Badge>;
    }
    if (configured) {
      return <Badge variant="outline" className="text-xs text-green-600 border-green-300 bg-green-50 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Konfiguriert</Badge>;
    }
    return <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 bg-amber-50 flex items-center gap-1"><XCircle className="w-3 h-3" /> Nicht konfiguriert</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            KI-Dienste Einstellungen
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-5">
            {missingService && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 flex gap-2 text-sm text-red-800">
                <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  {missingService === "openai"
                    ? "Für diese Funktion wird ein gültiger OpenAI API-Key benötigt. Bitte tragen Sie ihn unten ein."
                    : "Für diese Funktion wird ein gültiger Anthropic API-Key benötigt. Bitte tragen Sie ihn unten ein."}
                </span>
              </div>
            )}
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex gap-2 text-sm text-amber-800">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>API-Keys werden sicher auf dem Server gespeichert und nur für KI-Funktionen verwendet.</span>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Anthropic API-Key</Label>
                <ConfigBadge
                  configured={settings?.anthropicConfigured ?? false}
                  fromEnv={settings?.anthropicFromEnv ?? false}
                />
              </div>
              <p className="text-xs text-gray-500">Für Foto-Analyse, Diktat-Bereinigung und Dokumenten-Extraktion (Claude)</p>
              {settings?.anthropicApiKey && (
                <div className="text-xs text-gray-400 font-mono bg-gray-50 rounded px-2 py-1">
                  Gespeichert: {settings.anthropicApiKey}
                </div>
              )}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showAnthropic ? "text" : "password"}
                    placeholder={settings?.anthropicConfigured ? "Neuen Key eingeben (leer = unverändert)" : "sk-ant-..."}
                    value={anthropicInput}
                    onChange={(e) => setAnthropicInput(e.target.value)}
                    className="pr-10 font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAnthropic(!showAnthropic)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showAnthropic ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={testMutation.isPending || !settings?.anthropicConfigured && !anthropicInput}
                  onClick={() => handleTest("anthropic")}
                >
                  {testingService === "anthropic" ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    "Testen"
                  )}
                </Button>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-medium">OpenAI API-Key</Label>
                <ConfigBadge
                  configured={settings?.openaiConfigured ?? false}
                  fromEnv={settings?.openaiFromEnv ?? false}
                />
              </div>
              <p className="text-xs text-gray-500">Für Diktat-Transkription (Whisper) und Vision-Analyse</p>
              {settings?.openaiApiKey && (
                <div className="text-xs text-gray-400 font-mono bg-gray-50 rounded px-2 py-1">
                  Gespeichert: {settings.openaiApiKey}
                </div>
              )}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showOpenAI ? "text" : "password"}
                    placeholder={settings?.openaiConfigured ? "Neuen Key eingeben (leer = unverändert)" : "sk-..."}
                    value={openaiInput}
                    onChange={(e) => setOpenaiInput(e.target.value)}
                    className="pr-10 font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOpenAI(!showOpenAI)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showOpenAI ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={testMutation.isPending || !settings?.openaiConfigured && !openaiInput}
                  onClick={() => handleTest("openai")}
                >
                  {testingService === "openai" ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    "Testen"
                  )}
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={handleSave}
                disabled={saveMutation.isPending || (!anthropicInput && !openaiInput)}
              >
                {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Speichern
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
